import { supabase } from "@/integrations/supabase/client";
import { Message } from "./types";

/**
 * Get messages for a specific conversation
 */
export const getMessages = async (conversationId: string): Promise<Message[]> => {
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user) {
    return [];
  }

  const { data, error } = await supabase
    .from("chat_messages")
    .select()
    .eq('conversation_id', conversationId)
    .order('sent_at', { ascending: true });

  if (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }

  return data.map(message => ({
    ...message,
    is_sender: message.sender_profile_id === user.user?.id
  }));
};

/**
 * Send a message in a conversation
 */
export const sendMessage = async (conversationId: string, message: string, recipientId: string): Promise<Message> => {
  console.log("📨 [sendMessage] Iniciando envio de mensagem:", {
    conversationId,
    messageLength: message.length,
    recipientId: recipientId.substring(0, 8) + "..."
  });
  
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user) {
    console.error("❌ [sendMessage] Usuário não autenticado");
    throw new Error("User not authenticated");
  }

  console.log("✅ [sendMessage] Usuário autenticado:", user.user.id);

  try {
    console.log("🔍 [sendMessage] Verificando se conversa existe...");
    
    // First check if conversation exists
    const { data: conversationCheck, error: convError } = await supabase
      .from("chat_conversations")
      .select("id")
      .eq("id", conversationId)
      .single();
      
    if (convError) {
      console.error("❌ [sendMessage] Conversa não encontrada:", convError);
      throw new Error("Conversa não encontrada ou inacessível");
    }
    
    console.log("✅ [sendMessage] Conversa existe:", conversationCheck.id);
    
    // Set up message data with only valid columns
    const messageData = {
      conversation_id: conversationId,
      message,
      sender_profile_id: user.user.id
    };
    
    console.log("📝 [sendMessage] Dados da mensagem:", messageData);
    
    const { data, error } = await supabase
      .from("chat_messages")
      .insert(messageData)
      .select()
      .single();

    if (error) {
      console.error("❌ [sendMessage] Erro ao inserir mensagem:", error);
      throw error;
    }

    console.log("🎉 [sendMessage] Mensagem enviada com sucesso:", data.id);

    return {
      ...data,
      is_sender: true
    };
  } catch (error) {
    console.error("❌ [sendMessage] Erro geral:", error);
    throw error;
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (conversationId: string): Promise<void> => {
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user) {
    return;
  }

  const { error } = await supabase
    .from("chat_messages")
    .update({ is_read: true })
    .eq('conversation_id', conversationId)
    .eq('is_read', false)
    .neq('sender_profile_id', user.user.id);

  if (error) {
    console.error("Error marking messages as read:", error);
    throw error;
  }
};
